import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ThumbnailManifestType as VideoJsThumbnailManifestType,
  VideoPlayerHandle,
} from "@fronterahealth/frontera-ui-components";

import {
  SessionMediaReviewActivityLogEnum,
  ThumbnailManifestType,
  useCreateUpdateSessionMediaReviewActivityLogMutation,
} from "@api/graphql/types-and-hooks";
import { EmojiCommentButtonsv2 } from "@pages/VideoSessionsV2/components/EmojiComments/EmojiCommentButtonsv2";
import { EmojiCommentCard } from "@pages/VideoSessionsV2/components/EmojiComments/EmojiCommentCard";
import { MediaTimeline } from "@pages/VideoSessionsV2/components/MediaTimeline/MediaTimeline";
import { ZoomButtons } from "@pages/VideoSessionsV2/components/MediaTimeline/ZoomButtons";
import { getMomentsDataForTimeline } from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { VideoPlayerContent } from "@pages/VideoSessionsV2/components/VideoPlayerContent";
import { useEmojiMarkers } from "@pages/VideoSessionsV2/hooks/useEmojiMarkers";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

const localStorageKey = "video_progress";

interface VideoContainerProps {
  videoUrl: string;
  thumbnailManifest?: ThumbnailManifestType | null;
  onMarkerClick: (time: number, commentId: string) => void;
  onMomentMarkerClick: (startTime: number, endTime: number, momentId: string) => void;
  onTimelineClick: (time: number) => void;
  externalPlayerRef?: React.RefObject<VideoPlayerHandle | null>; // To handle player in the details view
  commentCount?: string;
  onDurationChange: (duration: number) => void;
  onSeeking?: () => void;
  onSeeked?: () => void;
  enabledFocus?: boolean;
  onPlayerReady?: () => void;
}

interface VideoProgressType {
  startTime: number;
  endTime: number;
  watchedSeconds: number;
  activityLogId?: string;
}
export const VideoContainer: React.FC<VideoContainerProps> = ({
  videoUrl,
  onMarkerClick,
  externalPlayerRef,
  commentCount,
  onDurationChange,
  // onTimelineClick,
  onSeeked,
  onSeeking,
  enabledFocus = true,
  onPlayerReady,
}) => {
  const localPlayerRef = useRef<VideoPlayerHandle>(null);
  const playerRef = externalPlayerRef || localPlayerRef;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTimeRef = useRef<number>(0);
  const playStartedTimeRef = useRef<number | null>(null);
  const watchedSecondsRef = useRef<number>(0);
  const mediaActivityLogIdRef = useRef<string | null>(null);
  const tickRef = useRef<number>(0);
  const callActivityLogAPIRef = useRef<() => Promise<void>>(async () => {});
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  const {
    getMediaByIdQuery: { isFetching },
    selectedMedia,
    setSelectedTab,
    selectedTab,
  } = useVideoSessionData();

  const [currentDuration, setCurrentDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [zoom, setZoom] = useState<number>(100);
  const [videoPlayerHeight, setVideoPlayerHeight] = useState(0);
  const [controlsHeight, setControlsHeight] = useState(0);

  const { isUserBCBA } = useAdminData();

  const mediaId = selectedMedia?.id || "";

  const saveSessionActivityMutation = useCreateUpdateSessionMediaReviewActivityLogMutation();
  const isSessionTracking = !!(isUserBCBA && selectedMedia && !selectedMedia.isViewed);

  const { goalMoments, expandAllGoals, activeMomentGoalId } = useVideoSessionData();

  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const toggleExpandRow = (goalId: string) => {
    if (expandedRowIds.includes(goalId)) {
      setExpandedRowIds((prev) => prev.filter((id) => id !== goalId));
    } else {
      setExpandedRowIds((prev) => [...prev, goalId]);
    }
  };

  useEffect(() => {
    if (activeMomentGoalId) {
      setExpandedRowIds((prev) => [...prev, activeMomentGoalId]);
    }
  }, [activeMomentGoalId]);

  const momentsData = useMemo(
    () => getMomentsDataForTimeline(goalMoments, selectedMedia?.totalDuration || 0),
    [goalMoments, selectedMedia?.totalDuration],
  );

  useEffect(() => {
    if (expandAllGoals) {
      setExpandedRowIds(momentsData.map((item) => item.id));
    } else {
      setExpandedRowIds([]);
    }
  }, [expandAllGoals, momentsData]);

  const handleMomentClicked = (startTime: number, endTime: number) => {
    if (selectedTab !== "moments") {
      setSelectedTab("moments");
    }
    if (playerRef.current) {
      playerRef.current.playInRange(startTime - 5, endTime);
    }
  };

  const getCurrentTime = () => {
    return playerRef.current?.player?.currentTime() || 0;
  };

  const handleDurationChange = (duration: number) => {
    setCurrentDuration(duration);
    onDurationChange(duration);
  };

  const { handleEmojiClick, selectedEmoji, handleCommentSubmit, closeCommentBox, emojiMarkers, isCommentSaving } =
    useEmojiMarkers({
      mediaId,
      videoDuration: currentDuration,
    });

  const handleMarkerClick = (timestamp: number, commentId: string) => {
    setSelectedTab("activity");
    onMarkerClick(timestamp, commentId);
  };

  // --- LocalStorage Helpers ---
  const saveProgressToLocalStorage = () => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      const allProgress: Record<string, VideoProgressType> = raw ? JSON.parse(raw) : {};
      allProgress[mediaId] = {
        startTime: playStartedTimeRef.current ?? 0,
        endTime: currentTimeRef.current,
        watchedSeconds: watchedSecondsRef.current,
        activityLogId: mediaActivityLogIdRef.current ?? undefined,
      };
      localStorage.setItem(localStorageKey, JSON.stringify(allProgress));
    } catch (e) {
      console.error("Error saving to localStorage", e);
    }
  };

  const getSavedProgress = (): VideoProgressType => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      if (!raw) return { startTime: 0, endTime: 0, watchedSeconds: 0 };
      const parsed = JSON.parse(raw);
      return parsed[mediaId] || { startTime: 0, endTime: 0, watchedSeconds: 0 };
    } catch (e) {
      console.error("Error reading from localStorage", e);
      return { startTime: 0, endTime: 0, watchedSeconds: 0 };
    }
  };

  const clearProgressFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      delete parsed[mediaId];
      localStorage.setItem(localStorageKey, JSON.stringify(parsed));
    } catch (e) {
      console.error("Error clearing from localStorage", e);
    }
  };
  const callActivityLogAPI = useCallback(async () => {
    if (selectedMedia) {
      if (selectedMedia.isViewed) return;
      try {
        await saveSessionActivityMutation
          .mutateAsync({
            input: {
              activityType: SessionMediaReviewActivityLogEnum.TimeSpent,
              duration: watchedSecondsRef.current,
              startTime: playStartedTimeRef.current ?? 0,
              endTime: currentTimeRef.current,
              mediaId: mediaId,
              id: mediaActivityLogIdRef.current || undefined,
            },
          })
          .then((data) => {
            if (data.createUpdateSessionMediaReviewActivityLog) {
              const newId = data?.createUpdateSessionMediaReviewActivityLog?.mediaActivityLog?.id;
              if (newId) mediaActivityLogIdRef.current = newId;
              saveProgressToLocalStorage();
            }
          });
      } catch (e) {
        console.error("Failed to log activity", e);
      }
    }
  }, [selectedMedia, mediaId]);

  // Keep the ref updated with the latest function
  useEffect(() => {
    callActivityLogAPIRef.current = callActivityLogAPI;
  }, [callActivityLogAPI]);

  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      watchedSecondsRef.current += 1;
      tickRef.current += 1;
      if (tickRef.current % 10 === 0) {
        callActivityLogAPIRef.current();
      }
    }, 1000);
  };

  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    tickRef.current = 0;
  };

  const handlePlay = () => {
    if (isSessionTracking) {
      if (playStartedTimeRef.current === null) {
        playStartedTimeRef.current = currentTimeRef.current;
      }

      startTimer();
    }
  };

  const handlePause = async () => {
    if (isSessionTracking) {
      stopTimer();
      await callActivityLogAPIRef.current();

      // Clear activity log tracking
      mediaActivityLogIdRef.current = null;
      playStartedTimeRef.current = currentTimeRef.current;
      watchedSecondsRef.current = watchedSecondsRef.current < 10 ? watchedSecondsRef.current : 0;

      // Clear persisted ID
      const raw = localStorage.getItem(localStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed[mediaId]) {
          delete parsed[mediaId].activityLogId;
          localStorage.setItem(localStorageKey, JSON.stringify(parsed));
        }
      }
    }
  };

  const handleEnded = () => {
    if (isSessionTracking) {
      stopTimer();
      callActivityLogAPIRef.current();
      mediaActivityLogIdRef.current = null;
      playStartedTimeRef.current = null;
      watchedSecondsRef.current = 0;
      currentTimeRef.current = 0;
      clearProgressFromLocalStorage();
    }
  };
  useEffect(() => {
    if (!isSessionTracking && !isFetching && selectedMedia && selectedMedia.isViewed) return;

    if (selectedMedia && !selectedMedia.isViewed) {
      // Load saved progress when component mounts or mediaId changes
      const saved = getSavedProgress();

      mediaActivityLogIdRef.current = saved.activityLogId || null;
      watchedSecondsRef.current = saved.watchedSeconds;
      currentTimeRef.current = saved.endTime;
      playStartedTimeRef.current = saved.startTime;

      setTimeout(() => {
        if (playerRef.current && saved.endTime > 0) {
          playerRef.current.seekTo(saved.endTime);
        }
      }, 500);
    }
    // Define cleanup function for unload or hidden state
    const handleExit = () => {
      if (
        isSessionTracking &&
        watchedSecondsRef.current > 0 &&
        !isFetching &&
        selectedMedia &&
        !selectedMedia.isViewed
      ) {
        stopTimer();
        callActivityLogAPIRef.current();
      }
    };
    // Add listeners
    const onBeforeUnload = () => {
      if (!isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Cleanup
    return () => {
      if (!isFetching && selectedMedia && !selectedMedia.isViewed) {
        handleExit();
      } // also called on component unmount
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isSessionTracking, selectedMedia]);

  const MemoizedVideoPlayerContent = useMemo(
    () => (
      <VideoPlayerContent
        thumbnailManifest={selectedMedia?.thumbnailManifest as VideoJsThumbnailManifestType}
        enabledFocus={enabledFocus}
        ref={playerRef}
        key={mediaId}
        videoUrl={videoUrl}
        onDurationChange={handleDurationChange}
        onSeeked={onSeeked}
        onTimeUpdate={(time) => {
          setCurrentTime(time);
          currentTimeRef.current = time;
        }}
        onSeeking={onSeeking}
        onPlayerReady={onPlayerReady}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnd={handleEnded}
        isResizable={true}
        initialHeight={600}
      />
    ),
    [videoUrl, mediaId, selectedMedia?.thumbnailManifest],
  );

  const handlePlayPause = (pause: boolean) => {
    const player = playerRef.current?.player;
    if (player) pause ? player.pause() : player.play();
  };

  const handleClose = () => {
    closeCommentBox();
    handlePlayPause(false);
  };

  const handleSubmit = (comment: string, timestamp: number) => {
    handleCommentSubmit(comment, timestamp);
    handlePlayPause(false);
  };

  // Calculate dynamic height for timeline
  const calculateTimelineHeight = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const headerHeight = 80; // Approximate header height
    const padding = 40; // Additional padding
    const usedHeight = videoPlayerHeight + controlsHeight + headerHeight + padding;
    return Math.max(200, viewportHeight - usedHeight); // Minimum 200px height
  }, [videoPlayerHeight, controlsHeight]);

  // Measure element heights
  useEffect(() => {
    const measureHeights = () => {
      if (videoPlayerRef.current) {
        const height = videoPlayerRef.current.offsetHeight;
        setVideoPlayerHeight(height);
      }
      if (controlsRef.current) {
        const height = controlsRef.current.offsetHeight;
        setControlsHeight(height);
      }
    };

    // Measure on mount and when selectedMedia changes
    measureHeights();

    // Use ResizeObserver to track height changes
    const resizeObserver = new ResizeObserver(() => {
      measureHeights();
    });

    if (videoPlayerRef.current) {
      resizeObserver.observe(videoPlayerRef.current);
    }
    if (controlsRef.current) {
      resizeObserver.observe(controlsRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [selectedMedia]);

  if (!selectedMedia) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0" ref={videoPlayerRef}>
        <div className="w-full lg:mx-auto" data-video-player>
          {MemoizedVideoPlayerContent}
          <div className="relative flex flex-col gap-2 lg:p-0 sm:p-6 p-4">
            {selectedEmoji && (
              <EmojiCommentCard
                selectedEmoji={selectedEmoji}
                currentTime={getCurrentTime()}
                onSubmit={handleSubmit}
                onClose={handleClose}
                isCommentSaving={isCommentSaving}
              />
            )}

            <div className="flex items-center px-4 h-[62px] pt-2 ">
              {/* Center */}
              <div className="flex-1 flex-col items-center justify-center">
                <EmojiCommentButtonsv2
                  onEmojiClick={handleEmojiClick}
                  disabled={currentDuration <= 0}
                  playerRef={playerRef}
                  commentCount={commentCount}
                  mediaId={selectedMedia.id}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-4 mr-4" ref={controlsRef}>
          <ZoomButtons zoom={zoom} setZoom={setZoom} />
        </div>
      </div>
      <div style={{ height: `${calculateTimelineHeight()}px` }}>
        <MediaTimeline
          data={momentsData}
          emojiMarkers={emojiMarkers}
          currentTime={currentTime}
          duration={selectedMedia?.totalDuration || 0}
          onMomentClicked={handleMomentClicked}
          zoomPercentValue={zoom}
          onMarkerClick={handleMarkerClick}
          expandAll={expandAllGoals}
          expandedRowIds={expandedRowIds}
          toggleExpandRow={toggleExpandRow}
        />
      </div>
    </div>
  );
};
